import React, { useEffect, useState } from "react";
import {
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    IconButton,
    Button,
    List,
    ListItem,
    ListItemText,
    Avatar,
    Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadIcon from "@mui/icons-material/Upload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import "./ProfilePage.css";
import useAuth from "../../hooks/useAuth";
import Loader from "../../global/loader/Loader";
import { BASE_URL, DASHBOARD_BASE_URL, FAILED, isEmpty, SUCCESS } from "../../constants/Constants";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const ProfilePage = ({ data }) => {

    const fieldLabels = {
        userName: "User Name",
        userEmail: "Email",
        phone: "Phone",
        jcId: "Unique Id",
        createdDate: "Created On",
        validity: "Validity(In days)",
        remainingValidity: "Remaining Validity",
        originalStartDate: "Original Start Date",
        startDate: "Start Date",
        shopCreatedAt: "Shop Created On",
    };

    const { activeShopHash, authUser } = useAuth();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({});
    const [methods, setMethods] = useState([]);
    const [newMethod, setNewMethod] = useState("");

    const [loader, setLoader] = useState(false);
    const [icon, setIcon] = useState(null);

    const canEdit = (formData?.isOwner ?? false) || (formData?.haveAdminRights ?? false);

    useEffect(() => {
        getProfile();
    }, []);

    const setFileUpload = async (file) => {
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("shopHash", activeShopHash.id);
            formData.append("type", "icon");

            const response = await axios.post(`${DASHBOARD_BASE_URL}set-file-upload`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            return response.data;
        } catch (err) {
            console.error("setFileUpload", err);
        }
        return { status: FAILED };
    }

    const getProfile = async () => {
        try {
            setLoader(true);
            const response = await axios.get(`${DASHBOARD_BASE_URL}get-profile?shopHash=${activeShopHash.id}&apiToken=${authUser.apiToken}`);
            if (response.data.status === SUCCESS) {
                setFormData(response?.data?.payLoad);
                setMethods(response.data.payLoad.onlinePayMethod);
            }
        } catch (err) {
            console.error("getProfile", err);
        } finally {
            setLoader(false);
        }
    }

    const handleUpdateProfile = async () => {
        try {
            let photPath = formData.companyIcon;
            setLoader(true);
            if (!isEmpty(icon)) {
                const imageResponse = await setFileUpload(icon);
                if (imageResponse.status === SUCCESS) {
                    //updating new photoPath
                    photPath = imageResponse.payLoad.path;
                    setIcon(null);
                }
            }
            const data = {
                payLoad: {
                    apiToken: authUser.apiToken,
                    shopHash: activeShopHash.id,
                    ...formData,
                    companyIcon: photPath,
                }
            };

            const response = await axios.post(`${DASHBOARD_BASE_URL}update-profile`, data);
            if (response.data.status === SUCCESS) {
                getProfile();
            }
        } catch (err) {
            console.error("handleUpdateProfile", err);
        } finally {
            setLoader(false);
        }
    };

    const handleAddMethod = () => {
        if (newMethod.trim()) {
            setMethods([...methods, newMethod.trim()]);
            setNewMethod("");
        }
    };

    const handleRemoveMethod = (index) => {
        const updated = [...methods];
        updated.splice(index, 1);
        setMethods(updated);
    };

    const handleChange = (key, value) => {
        setFormData({ ...formData, [key]: value });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) setIcon(file);
    };


    return (

        <Card className="profile-card">
            <CardContent>
                {/* Header */}
                <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                        <Button
                            variant="contained"
                            color="primary"
                            onPointerDown={() => navigate("/config")}
                        >
                            Configuration Table
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Button
                            variant="contained"
                            color="primary"
                            onPointerDown={() => navigate("/recharge")}
                        >
                            Service Recharge Portal
                        </Button>
                    </Grid>
                </Grid>
                <div className="profile-header">
                    {
                        !isEmpty(icon) &&
                        <Avatar
                            src={URL.createObjectURL(icon)}
                            alt="Company Logo"
                            sx={{ width: 80, height: 80 }}
                        />
                    }
                    {
                        (isEmpty(icon) && !isEmpty(formData?.companyIcon)) &&
                        <Avatar
                            src={`${BASE_URL}/files/${formData?.companyIcon}`}
                            alt="Company Logo"
                            sx={{ width: 80, height: 80 }}
                        />
                    }
                    <div className="profile-header-info">
                        <Typography variant="h5" className="company-name">
                            {formData?.shop ?? ""}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {`${formData?.location ?? ""}, ${formData?.city ?? ""}, ${formData?.state ?? ""}, ${formData?.country ?? ""} - ${formData?.pinCode ?? ""}`}
                        </Typography>
                    </div>
                    <Tooltip title="Browse company logo">
                        <Button
                            variant="contained"
                            component="label"
                            startIcon={<UploadIcon />}
                            sx={{ mb: 2, mr: 2 }}
                        >
                            Browse Icon
                            <input hidden type="file"
                                accept=".jpg,.jpeg,.png"
                                onChange={handleFileChange} />
                        </Button>
                    </Tooltip>
                </div>

                <Grid container spacing={2} sx={{ mt: 2 }}>
                    {
                        loader && <Loader />
                    }
                    {
                        canEdit &&
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Hotel Name"
                                value={formData?.shop || ""}
                                onChange={(e) =>
                                    handleChange("shop", e.target.value)
                                }
                                fullWidth
                                disabled={!canEdit}
                                className={!canEdit ? "greyed" : ""}
                            />
                        </Grid>
                    }
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Company Name(optional)"
                            value={formData?.companyName || ""}
                            onChange={(e) =>
                                handleChange("companyName", e.target.value)
                            }
                            fullWidth
                            disabled={!canEdit}
                            className={!canEdit ? "greyed" : ""}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Company Email"
                            value={formData?.companyMail || ""}
                            onChange={(e) =>
                                handleChange("companyMail", e.target.value)
                            }
                            fullWidth
                            disabled={!canEdit}
                            className={!canEdit ? "greyed" : ""}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Company Web Address"
                            value={formData?.companyWebAddress || ""}
                            onChange={(e) =>
                                handleChange("companyWebAddress", e.target.value)
                            }
                            fullWidth
                            disabled={!canEdit}
                            className={!canEdit ? "greyed" : ""}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Address"
                            value={formData?.location || ""}
                            onChange={(e) =>
                                handleChange("location", e.target.value)
                            }
                            fullWidth
                            disabled={!canEdit}
                            className={!canEdit ? "greyed" : ""}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="City"
                            value={formData?.city || ""}
                            onChange={(e) =>
                                handleChange("city", e.target.value)
                            }
                            fullWidth
                            disabled={!canEdit}
                            className={!canEdit ? "greyed" : ""}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="State"
                            value={formData?.state || ""}
                            onChange={(e) =>
                                handleChange("state", e.target.value)
                            }
                            fullWidth
                            disabled={!canEdit}
                            className={!canEdit ? "greyed" : ""}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Pin Code"
                            value={formData?.pinCode || ""}
                            onChange={(e) =>
                                handleChange("pinCode", e.target.value)
                            }
                            fullWidth
                            disabled={!canEdit}
                            className={!canEdit ? "greyed" : ""}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Country"
                            value={formData?.country || ""}
                            onChange={(e) =>
                                handleChange("country", e.target.value)
                            }
                            fullWidth
                            disabled={!canEdit}
                            className={!canEdit ? "greyed" : ""}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Pan Card"
                            value={formData?.panCard || ""}
                            onChange={(e) => handleChange("panCard", e.target.value)}
                            fullWidth
                            disabled={!canEdit}
                            className={!canEdit ? "greyed" : ""}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            label="GST Information"
                            value={formData?.gstDetail || ""}
                            onChange={(e) => handleChange("gstDetail", e.target.value)}
                            fullWidth
                            disabled={!canEdit}
                            className={!canEdit ? "greyed" : ""}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" className="section-title">
                            Personel Information
                        </Typography>
                    </Grid>
                    {[
                        "userName",
                        "userEmail",
                        "phone",
                        "jcId",
                        "createdDate",
                        "remainingValidity",
                        "startDate",
                        "originalStartDate",
                        "validity",
                    ].map((field) => (
                        <Grid item xs={12} md={6} key={field}>
                            <TextField
                                label={fieldLabels[field] || field}
                                value={formData?.[field] || ""}
                                fullWidth
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                    ))}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" className="section-title">
                            Features/Roles
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" className="section-title">
                            Features
                        </Typography>
                        <div className="map-list">
                            {Object.entries(formData?.packages || {}).map(([key, val]) => (
                                <div
                                    key={key}
                                    className={`map-item ${!val ? "inactive-item" : ""}`}
                                >
                                    <span>{key}</span>
                                    {val ? (
                                        <CheckCircleIcon color="success" fontSize="small" />
                                    ) : (
                                        <CancelIcon color="disabled" fontSize="small" />
                                    )}
                                </div>
                            ))}
                        </div>
                    </Grid>

                    {/* Roles */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" className="section-title">
                            Roles
                        </Typography>
                        <div className="map-list">
                            {Object.entries(formData?.roles || {}).map(([key, val]) => (
                                <div
                                    key={key}
                                    className={`map-item ${!val ? "inactive-item" : ""}`}
                                >
                                    <span>{key}</span>
                                    {val ? (
                                        <CheckCircleIcon color="success" fontSize="small" />
                                    ) : (
                                        <CancelIcon color="disabled" fontSize="small" />
                                    )}
                                </div>
                            ))}
                        </div>
                    </Grid>

                    {/* Online Pay Method */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" className="section-title">
                            Online Pay Methods
                        </Typography>
                        <List dense>
                            {methods.map((m, i) => (
                                <ListItem
                                    key={i}
                                    className={!canEdit ? "greyed" : ""}
                                    secondaryAction={
                                        canEdit && (
                                            <IconButton onClick={() => handleRemoveMethod(i)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        )
                                    }
                                >
                                    <ListItemText primary={m} />
                                </ListItem>
                            ))}
                        </List>

                        <div className="add-method">
                            <TextField
                                size="small"
                                placeholder="Add new method"
                                value={newMethod}
                                onChange={(e) => setNewMethod(e.target.value)}
                                disabled={!canEdit}
                                className={!canEdit ? "greyed" : ""}
                            />
                            <IconButton onClick={handleAddMethod} disabled={!canEdit}>
                                <AddIcon />
                            </IconButton>
                        </div>
                    </Grid>
                </Grid>

                {/* Update Button */}
                <div className="update-btn">
                    <Button
                        variant="contained"
                        color="primary"
                        onPointerDown={() => handleUpdateProfile()}
                    >
                        Update Profile
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

export default ProfilePage;
